/**
 * Filters PDP variant options using product metafields:
 * - custom.code_chains (FIFO active code per option combo) — preferred
 * - custom.variant_code_groups (legacy main/sub groups) — fallback
 */
(function () {
  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  }

  function getVariantOptions(variant) {
    return [variant.option1, variant.option2, variant.option3].filter((o) => o != null && o !== '');
  }

  function getSelectedOptions(variantSelects) {
    return Array.from(variantSelects.querySelectorAll('select, fieldset')).map((element) => {
      if (element.tagName === 'SELECT') return element.value;
      if (element.tagName === 'FIELDSET') {
        const checked = Array.from(element.querySelectorAll('input')).find((radio) => radio.checked);
        return checked ? checked.value : null;
      }
      return null;
    });
  }

  function findVariantByOptions(variants, options) {
    return variants.find((variant) => {
      const vOpts = getVariantOptions(variant);
      if (vOpts.length !== options.length) return false;
      return vOpts.every((opt, i) => opt === options[i]);
    });
  }

  function countMatchingOptions(variant, options, excludePosition) {
    const vOpts = getVariantOptions(variant);
    let count = 0;
    for (let i = 0; i < options.length; i++) {
      if (i === excludePosition) continue;
      if (options[i] != null && vOpts[i] === options[i]) count++;
    }
    return count;
  }

  function findBestAllowedVariant(variants, allowedIds, selectedOptions, changedPosition, newValue) {
    const candidates = variants.filter((variant) => {
      if (!allowedIds.has(Number(variant.id))) return false;
      const vOpts = getVariantOptions(variant);
      return vOpts[changedPosition] === newValue;
    });

    if (!candidates.length) return null;

    candidates.sort((a, b) => {
      const scoreDiff =
        countMatchingOptions(b, selectedOptions, changedPosition) -
        countMatchingOptions(a, selectedOptions, changedPosition);
      if (scoreDiff !== 0) return scoreDiff;
      return Number(a.id) - Number(b.id);
    });

    return candidates[0];
  }

  function variantMatchesChainOptions(variant, chain) {
    const optionValues = chain.optionValues || {};
    const vOpts = getVariantOptions(variant);
    const entries = Object.entries(optionValues);
    if (!entries.length) return false;

    return entries.every(([name, value]) => {
      const containers = document.querySelectorAll('variant-selects select, variant-selects fieldset');
      let position = -1;
      containers.forEach((container, idx) => {
        let label = '';
        if (container.tagName === 'SELECT') {
          const formInput = container.closest('.product-form__input');
          const legend = formInput?.querySelector('.form__label');
          label = legend?.textContent?.replace(':', '').trim() || '';
        } else if (container.tagName === 'FIELDSET') {
          label = container.querySelector('.form__label')?.textContent?.replace(':', '').trim() || '';
        }
        if (label.toLowerCase() === String(name).trim().toLowerCase()) {
          position = idx;
        }
      });

      if (position < 0) {
        return vOpts.some((opt) => opt === value);
      }
      return vOpts[position] === value;
    });
  }

  function getActiveCode(chain) {
    if (!chain?.codes?.length) return null;
    const idx = Number(chain.activeIndex) || 0;
    if (idx >= chain.codes.length) return null;
    return chain.codes[idx] || null;
  }

  function getAllowedVariantIdsFromChains(chains, variants) {
    const allChainSkus = new Set();
    (chains || []).forEach((chain) => {
      (chain.codes || []).forEach((code) => allChainSkus.add(String(code)));
    });

    const allowed = new Set();
    (variants || []).forEach((variant) => {
      const sku = String(variant.sku || '').trim();
      if (!allChainSkus.has(sku)) {
        allowed.add(Number(variant.id));
        return;
      }
      const matchingChain = (chains || []).find(
        (chain) => getActiveCode(chain) === sku && variantMatchesChainOptions(variant, chain)
      );
      if (matchingChain) {
        allowed.add(Number(variant.id));
      }
    });

    return allowed;
  }

  function getAllowedVariantIds(groups, variants, selectedOptions, chains) {
    if (chains?.chains?.length) {
      return getAllowedVariantIdsFromChains(chains.chains, variants);
    }

    const mainIds = new Set(groups.map((g) => Number(g.mainVariantId)));
    const allSubIds = new Set(groups.flatMap((g) => (g.subVariantIds || []).map(Number)));
    const current = findVariantByOptions(variants, selectedOptions);
    const currentId = current ? Number(current.id) : null;

    if (!currentId) {
      return new Set(variants.map((v) => Number(v.id)));
    }

    if (mainIds.has(currentId)) {
      const group = groups.find((g) => Number(g.mainVariantId) === currentId);
      if (!group) return new Set([currentId]);
      return new Set([currentId, ...(group.subVariantIds || []).map(Number)]);
    }

    if (allSubIds.has(currentId)) {
      const group = groups.find((g) => (g.subVariantIds || []).map(Number).includes(currentId));
      if (!group) return new Set([currentId]);
      return new Set([Number(group.mainVariantId), ...(group.subVariantIds || []).map(Number)]);
    }

    return new Set(variants.map((v) => Number(v.id)));
  }

  function setControlVisible(control, visible) {
    const tag = control.tagName;
    if (tag === 'OPTION') {
      control.hidden = !visible;
      control.disabled = !visible;
      return;
    }
    const label = control.id
      ? control.closest('fieldset')?.querySelector(`label[for="${control.id}"]`)
      : control.closest('label');
    const wrap = label || control;
    wrap.classList.toggle('variant-code-groups-hidden', !visible);
    control.disabled = !visible;
  }

  function updateFieldsetLegend(fieldset, value) {
    const legendSpan = fieldset.querySelector('.form__label span');
    if (legendSpan) legendSpan.innerHTML = value;
    const swatchSpan = fieldset.querySelector('[data-selected-swatch-value]');
    if (swatchSpan) swatchSpan.innerHTML = value;
  }

  function updateDropdownSwatch(select, value) {
    const inputWrapper = select.closest('.product-form__input');
    const legendSpan = inputWrapper?.querySelector('[data-selected-swatch-value]');
    if (legendSpan) legendSpan.textContent = value;

    const selectedOption = Array.from(select.options).find((option) => option.value === value);
    const swatchEl = inputWrapper?.querySelector('[data-selected-dropdown-swatch] .swatch');
    if (!selectedOption || !swatchEl) return;

    const swatchValue = selectedOption.dataset.optionSwatchValue;
    if (swatchValue) {
      swatchEl.style.setProperty('--swatch--background', swatchValue);
      swatchEl.classList.remove('swatch--unavailable');
    } else {
      swatchEl.style.setProperty('--swatch--background', 'unset');
      swatchEl.classList.add('swatch--unavailable');
    }

    swatchEl.style.setProperty(
      '--swatch-focal-point',
      selectedOption.dataset.optionSwatchFocalPoint || 'unset'
    );
  }

  function syncPickersToOptions(variantSelects, options) {
    const containers = Array.from(variantSelects.querySelectorAll('select, fieldset'));

    containers.forEach((container, position) => {
      const value = options[position];
      if (value == null) return;

      if (container.tagName === 'SELECT') {
        if (container.value === value) return;
        container.value = value;
        Array.from(container.options).forEach((option) => {
          if (option.value === value) {
            option.setAttribute('selected', 'selected');
          } else {
            option.removeAttribute('selected');
          }
        });
        updateDropdownSwatch(container, value);
        return;
      }

      if (container.tagName === 'FIELDSET') {
        Array.from(container.querySelectorAll('input[type="radio"]')).forEach((radio) => {
          const isSelected = radio.value === value;
          radio.checked = isSelected;
          const label =
            (radio.id && variantSelects.querySelector(`label[for="${radio.id}"]`)) ||
            radio.closest('label');
          label?.classList.toggle('selected', isSelected);
        });
        updateFieldsetLegend(container, value);
      }
    });
  }

  function readFilterConfig(variantSelects) {
    const chainsEl = variantSelects.querySelector('[data-code-chains]');
    const groupsEl = variantSelects.querySelector('[data-variant-code-groups]');
    const variantsEl = variantSelects.querySelector('[data-product-variants]');
    const chains = parseJson(chainsEl);
    const groupsConfig = parseJson(groupsEl);
    const variants = parseJson(variantsEl);
    const groups = groupsConfig?.groups || [];
    const hasChains = Boolean(chains?.chains?.length);
    const hasGroups = Boolean(groups.length);
    if ((!hasChains && !hasGroups) || !variants?.length) return null;
    return { chains, groups, variants, hasChains };
  }

  function reconcileVariantSelection(variantSelects, event) {
    const target = event.target;
    if (!target.matches('select, input[type="radio"]')) return;

    const config = readFilterConfig(variantSelects);
    if (!config) return;

    const { chains, groups, variants, hasChains } = config;
    const optionContainers = Array.from(variantSelects.querySelectorAll('select, fieldset'));
    const changedContainer = target.tagName === 'SELECT' ? target : target.closest('fieldset');
    const changedPosition = optionContainers.indexOf(changedContainer);
    if (changedPosition < 0) return;

    const newValue = target.value;
    const selectedOptions = getSelectedOptions(variantSelects);
    const allowedIds = getAllowedVariantIds(groups, variants, selectedOptions, hasChains ? chains : null);
    const best = findBestAllowedVariant(variants, allowedIds, selectedOptions, changedPosition, newValue);
    if (!best) return;

    const bestOptions = getVariantOptions(best);
    const needsSync = bestOptions.some((opt, i) => selectedOptions[i] !== opt);
    if (!needsSync) return;

    syncPickersToOptions(variantSelects, bestOptions);

    const selectedVariantEl = variantSelects.querySelector('[data-selected-variant]');
    if (selectedVariantEl) selectedVariantEl.textContent = JSON.stringify(best);
  }

  function applyFiltering(variantSelects) {
    const config = readFilterConfig(variantSelects);
    if (!config) return;

    const { chains, groups, variants, hasChains } = config;
    const selectedOptions = getSelectedOptions(variantSelects);
    const allowedIds = getAllowedVariantIds(groups, variants, selectedOptions, hasChains ? chains : null);
    const allowedVariants = variants.filter((variant) => allowedIds.has(Number(variant.id)));

    const optionContainers = Array.from(variantSelects.querySelectorAll('select, fieldset'));
    optionContainers.forEach((container, position) => {
      const controls =
        container.tagName === 'SELECT'
          ? Array.from(container.querySelectorAll('option'))
          : Array.from(container.querySelectorAll('input[type="radio"]'));

      controls.forEach((control) => {
        const value = control.value;
        const visible = allowedVariants.some((variant) => getVariantOptions(variant)[position] === value);
        setControlVisible(control, visible);
      });
    });
  }

  function onVariantSelectsChange(variantSelects, event) {
    reconcileVariantSelection(variantSelects, event);
    applyFiltering(variantSelects);
  }

  function refreshAll() {
    document
      .querySelectorAll('variant-selects[data-variant-code-groups-enabled]')
      .forEach(applyFiltering);
  }

  function boot() {
    refreshAll();

    document.addEventListener(
      'change',
      (event) => {
        const variantSelects = event.target.closest('variant-selects[data-variant-code-groups-enabled]');
        if (!variantSelects || !event.target.matches('select, input[type="radio"]')) return;
        onVariantSelectsChange(variantSelects, event);
      },
      true
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, refreshAll);
      subscribe(PUB_SUB_EVENTS.variantChange, refreshAll);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
